import React, { useCallback, useEffect, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { FiTrendingUp } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import useCurrency from '../../hooks/useCurrency';
import { colors } from '../../theme/colors';
import { getSalesOverview, getSalesOverviewBreakdown } from '../../utils/dashboardWidgetData';

// Brand chart colors. Maroon is the series color; the area fades to transparent.
const MAROON = colors.primary.main;

const PERIODS = [
  { id: 'day', label: 'Day', subtitle: 'Last 30 days' },
  { id: 'week', label: 'Week', subtitle: 'Last 12 weeks' },
  { id: 'month', label: 'Month', subtitle: 'Financial year to date' },
  { id: 'year', label: 'Year', subtitle: 'Financial year' }
];

const TABS = [
  { id: 'items', label: 'Top Items', key: 'topItems', empty: 'No items sold in this period yet.' },
  { id: 'customers', label: 'Top Customers', key: 'topCustomers', empty: 'No customer sales in this period yet.' },
  { id: 'regions', label: 'Top Regions', key: 'topRegions', empty: 'No region data for this period yet.' }
];

const ChartTip = ({ active, payload, label, formatCurrency }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload || {};
  return (
    <TipBox>
      <TipLabel>{label}</TipLabel>
      <TipRow>
        <TipDot $c={MAROON} />
        <TipName>Revenue</TipName>
        <TipValue>{formatCurrency(p.revenue || 0)}</TipValue>
      </TipRow>
      <TipMuted>{p.invoices || 0} invoice{(p.invoices || 0) === 1 ? '' : 's'}</TipMuted>
    </TipBox>
  );
};

const SalesOverviewWidget = () => {
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  const { formatCurrency } = useCurrency();
  const companyId = activeCompany?.id;
  const branchId = activeBranch && activeBranch.id !== 'all' ? activeBranch.id : null;

  const [period, setPeriod] = useState('month');
  const [tab, setTab] = useState('items');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Top-5 lists are NOT part of the dashboard load any more — they need whole
  // sale documents (the single biggest Firestore cost on this page), so they
  // are fetched only when the user opens a tab. Kept per period/branch/FY scope
  // so switching back and forth never re-buys them.
  const [breakdowns, setBreakdowns] = useState({});
  const [bdLoading, setBdLoading] = useState(false);
  const [bdError, setBdError] = useState(false);

  const scopeKey = `${companyId || ''}|${activeFinancialYear?.id || 'nofy'}|${period}|${branchId || 'all'}`;
  // When the graph itself had to fall back to the document scan it already
  // carries the lists — no second fetch in that case.
  const breakdown = (data && data.breakdownLoaded) ? data : breakdowns[scopeKey];

  const loadBreakdown = useCallback(async () => {
    if (!companyId) return;
    if (breakdown || bdLoading) return;
    setBdLoading(true);
    setBdError(false);
    try {
      const res = await getSalesOverviewBreakdown(companyId, activeFinancialYear, period, branchId);
      setBreakdowns(prev => ({ ...prev, [scopeKey]: res || {} }));
    } catch (e) {
      setBdError(true);
    } finally {
      setBdLoading(false);
    }
  }, [companyId, activeFinancialYear, period, branchId, scopeKey, breakdown, bdLoading]);

  useEffect(() => {
    let cancelled = false;
    if (!companyId) { setLoading(false); return undefined; }
    setLoading(true);
    (async () => {
      try {
        const res = await getSalesOverview(companyId, activeFinancialYear, period, branchId);
        if (!cancelled) setData(res || null);
      } catch (e) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, activeFinancialYear?.id, period, branchId]);

  if (!companyId) return null;

  const periodMeta = PERIODS.find(p => p.id === period) || PERIODS[2];
  const hasData = !!data && (data.invoiceCount > 0 || data.totalRevenue !== 0);
  const activeTab = TABS.find(t => t.id === tab) || TABS[0];
  const list = (breakdown && Array.isArray(breakdown[activeTab.key])) ? breakdown[activeTab.key] : [];
  const maxVal = list.length ? Math.max(list[0].value || 0, 1) : 1;

  const openTab = (id) => { setTab(id); loadBreakdown(); };

  return (
    <Card>
      <Header>
        <HeaderLeft>
          <IconWrap><FiTrendingUp size={15} /></IconWrap>
          <Title>Sales Overview</Title>
        </HeaderLeft>
        <PillGroup>
          {PERIODS.map(p => (
            <Pill key={p.id} $active={period === p.id} onClick={() => setPeriod(p.id)}>
              {p.label}
            </Pill>
          ))}
        </PillGroup>
      </Header>

      {loading && !data ? (
        <SkeletonBody />
      ) : !hasData ? (
        <>
          <Kpi>
            <KpiValue>{formatCurrency(0)}</KpiValue>
            <KpiSub>0 invoices &middot; {periodMeta.subtitle}</KpiSub>
          </Kpi>
          <EmptyBox>No sales recorded for this period yet — new invoices will appear here.</EmptyBox>
        </>
      ) : (
        <Body $dim={loading}>
          <Kpi>
            <KpiValue>{formatCurrency(data.totalRevenue || 0)}</KpiValue>
            <KpiSub>
              {data.invoiceCount || 0} invoice{(data.invoiceCount || 0) === 1 ? '' : 's'} &middot; {periodMeta.subtitle}
            </KpiSub>
          </Kpi>

          <ChartZone>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={data.series || []} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="amalSalesOvArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={MAROON} stopOpacity={0.26} />
                    <stop offset="100%" stopColor={MAROON} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} vertical={false} />
                <XAxis
                  dataKey="label"
                  stroke={colors.text.secondary}
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  minTickGap={18}
                  interval="preserveStartEnd"
                />
                <YAxis
                  stroke={colors.text.secondary}
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  width={62}
                  tickFormatter={(v) => formatCurrency(v, { compact: true })}
                />
                <Tooltip content={<ChartTip formatCurrency={formatCurrency} />} cursor={{ stroke: colors.neutral[300], strokeDasharray: '3 3' }} />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke={MAROON}
                  strokeWidth={2}
                  fill="url(#amalSalesOvArea)"
                  activeDot={{ r: 4, strokeWidth: 2, stroke: '#fff' }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartZone>

          <TabRow>
            {TABS.map(t => (
              <TabBtn key={t.id} $active={tab === t.id} onClick={() => openTab(t.id)}>
                {t.label}
              </TabBtn>
            ))}
          </TabRow>

          <RankList>
            {bdLoading ? (
              <EmptyBox $compact>Loading {activeTab.label.toLowerCase()}…</EmptyBox>
            ) : bdError ? (
              <EmptyBox $compact>
                Could not load {activeTab.label.toLowerCase()}.{' '}
                <LoadLink type="button" onClick={loadBreakdown}>Try again</LoadLink>
              </EmptyBox>
            ) : !breakdown ? (
              <EmptyBox $compact>
                <LoadLink type="button" onClick={loadBreakdown}>Show {activeTab.label}</LoadLink>
              </EmptyBox>
            ) : list.length === 0 ? (
              <EmptyBox $compact>{activeTab.empty}</EmptyBox>
            ) : (
              list.map((row, i) => (
                <RankRow key={`${activeTab.id}-${row.name}`}>
                  <RankTop>
                    <RankNum>{i + 1}</RankNum>
                    <RankName title={row.name}>{row.name}</RankName>
                    <RankAmount>{formatCurrency(row.value || 0)}</RankAmount>
                  </RankTop>
                  <RankTrack>
                    <RankFill style={{ width: `${Math.max(4, Math.round(((row.value || 0) / maxVal) * 100))}%` }} />
                  </RankTrack>
                </RankRow>
              ))
            )}
          </RankList>
        </Body>
      )}
    </Card>
  );
};

export default SalesOverviewWidget;

// ── skeleton ────────────────────────────────────────────────────────────────
const SkeletonBody = () => (
  <div>
    <SkelBlock $w="180px" $h="30px" style={{ marginBottom: 6 }} />
    <SkelBlock $w="130px" $h="14px" style={{ marginBottom: 16 }} />
    <SkelBlock $w="100%" $h="260px" style={{ marginBottom: 16 }} />
    <SkelBlock $w="60%" $h="30px" style={{ marginBottom: 12 }} />
    {[0, 1, 2, 3, 4].map(i => (
      <SkelBlock key={i} $w="100%" $h="30px" style={{ marginBottom: 10 }} />
    ))}
  </div>
);

// ── styled ──────────────────────────────────────────────────────────────────
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid ${colors.neutral[200]};
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;
const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  gap: 10px; flex-wrap: wrap;
`;
const HeaderLeft = styled.div`
  display: flex; align-items: center; gap: 9px;
`;
const IconWrap = styled.div`
  width: 28px; height: 28px; border-radius: 8px;
  background: ${MAROON}12;
  color: ${MAROON};
  display: inline-flex; align-items: center; justify-content: center;
  flex-shrink: 0;
`;
const Title = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${colors.text.primary};
`;
const PillGroup = styled.div`
  display: inline-flex; gap: 2px;
  background: ${MAROON}0d;
  border: 1px solid ${MAROON}22;
  border-radius: 10px;
  padding: 3px;
`;
const Pill = styled.button`
  border: none;
  border-radius: 7px;
  padding: 4px 12px;
  font-size: 11.5px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.18s ease;
  background: ${props => (props.$active ? MAROON : 'transparent')};
  color: ${props => (props.$active ? '#fff' : MAROON)};
  box-shadow: ${props => (props.$active ? '0 1px 3px rgba(128,0,32,0.35)' : 'none')};

  &:hover {
    background: ${props => (props.$active ? MAROON : `${MAROON}1a`)};
  }
`;
const Body = styled.div`
  display: flex; flex-direction: column; gap: 14px;
  transition: opacity 0.2s ease;
  ${props => props.$dim && css`opacity: 0.55; pointer-events: none;`}
`;
const Kpi = styled.div`
  display: flex; flex-direction: column; gap: 2px;
`;
const KpiValue = styled.div`
  font-size: 27px;
  font-weight: 700;
  letter-spacing: -0.5px;
  color: ${colors.text.primary};
  line-height: 1.15;
`;
const KpiSub = styled.div`
  font-size: 12.5px;
  font-weight: 500;
  color: ${colors.text.secondary};
`;
const ChartZone = styled.div`
  width: 100%;
  margin-left: -8px;
`;
const TabRow = styled.div`
  display: flex; gap: 2px;
  border-bottom: 1px solid ${colors.neutral[200]};
`;
const TabBtn = styled.button`
  background: transparent;
  border: none;
  border-bottom: 2px solid ${props => (props.$active ? MAROON : 'transparent')};
  color: ${props => (props.$active ? MAROON : colors.text.secondary)};
  font-size: 12.5px;
  font-weight: ${props => (props.$active ? 700 : 600)};
  padding: 7px 12px 8px;
  margin-bottom: -1px;
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover { color: ${MAROON}; }
`;
const RankList = styled.div`
  display: flex; flex-direction: column; gap: 11px;
  min-height: 205px;
`;
const RankRow = styled.div`
  display: flex; flex-direction: column; gap: 5px;
`;
const RankTop = styled.div`
  display: flex; align-items: center; gap: 10px;
`;
const RankNum = styled.div`
  width: 20px; height: 20px;
  border-radius: 6px;
  background: ${MAROON}10;
  color: ${MAROON};
  font-size: 10.5px;
  font-weight: 700;
  display: inline-flex; align-items: center; justify-content: center;
  flex-shrink: 0;
`;
const RankName = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${colors.text.primary};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;
const RankAmount = styled.div`
  font-size: 12.5px;
  font-weight: 700;
  color: ${colors.text.primary};
  flex-shrink: 0;
`;
const RankTrack = styled.div`
  width: 100%;
  height: 4px;
  border-radius: 99px;
  background: ${colors.neutral[100]};
  overflow: hidden;
`;
const RankFill = styled.div`
  height: 100%;
  border-radius: 99px;
  background: linear-gradient(90deg, ${colors.primary.dark}, ${colors.primary.light});
  transition: width 0.45s ease;
`;
const LoadLink = styled.button`
  background: transparent;
  border: none;
  padding: 0;
  color: ${MAROON};
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  text-decoration: underline;

  &:hover { opacity: 0.8; }
`;
const EmptyBox = styled.div`
  padding: ${props => (props.$compact ? '26px 12px' : '48px 16px')};
  text-align: center;
  font-size: 12.5px;
  font-weight: 500;
  color: ${colors.text.secondary};
`;
const SkelBlock = styled.div`
  width: ${props => props.$w || '100%'};
  height: ${props => props.$h || '16px'};
  border-radius: 8px;
  background: ${colors.neutral[100]};
  animation: ${pulse} 1.4s ease-in-out infinite;
`;
const TipBox = styled.div`
  background: #fff;
  border: 1px solid ${colors.neutral[200]};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  padding: 9px 12px;
  min-width: 140px;
`;
const TipLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: ${colors.text.secondary};
  margin-bottom: 5px;
`;
const TipRow = styled.div`
  display: flex; align-items: center; gap: 6px;
  font-size: 12px;
`;
const TipDot = styled.span`
  width: 8px; height: 8px; border-radius: 50%;
  background: ${props => props.$c};
  flex-shrink: 0;
`;
const TipName = styled.span`
  color: ${colors.text.secondary};
  font-weight: 500;
  flex: 1;
`;
const TipValue = styled.b`
  color: ${colors.text.primary};
  font-weight: 700;
`;
const TipMuted = styled.div`
  margin-top: 4px;
  font-size: 11px;
  color: ${colors.text.tertiary};
`;
